import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PublicUser } from '@hub/shared';
import { api, ApiError } from '../api/client';

/** A password check either completes the sign-in, or asks for a second factor. */
export type LoginOutcome =
  | { status: 'signed-in' }
  | { status: 'two-factor-required'; challengeToken: string };

interface AuthState {
  user: PublicUser | null;
  loading: boolean;
  isAdmin: boolean;
  login: (login: string, password: string) => Promise<LoginOutcome>;
  completeTwoFactor: (challengeToken: string, code: string) => Promise<void>;
  register: (email: string, password: string, inviteCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<{ user: PublicUser }>('/api/auth/me');
      setUser(res.user);
    } catch (err) {
      // 401 simply means "not signed in" — anything else is worth surfacing
      if (!(err instanceof ApiError) || err.status !== 401) {
        console.error('[auth] Could not resolve the current user:', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const login = useCallback(async (loginId: string, password: string): Promise<LoginOutcome> => {
    const res = await api.post<{
      user?: PublicUser;
      twoFactorRequired?: boolean;
      challengeToken?: string;
    }>('/api/auth/login', { login: loginId, password });

    if (res.twoFactorRequired && res.challengeToken) {
      // No session yet — the challenge is worthless until a code is accepted.
      return { status: 'two-factor-required', challengeToken: res.challengeToken };
    }
    setUser(res.user ?? null);
    return { status: 'signed-in' };
  }, []);

  const completeTwoFactor = useCallback(async (challengeToken: string, code: string) => {
    const res = await api.post<{ user: PublicUser }>('/api/auth/login/2fa', { challengeToken, code });
    setUser(res.user);
  }, []);

  const register = useCallback(async (email: string, password: string, inviteCode?: string) => {
    const res = await api.post<{ user: PublicUser }>('/api/auth/register', { email, password, inviteCode });
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout');
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(() => ({
    user,
    loading,
    isAdmin: user?.role === 'admin',
    login,
    completeTwoFactor,
    register,
    logout,
    refresh,
  }), [user, loading, login, completeTwoFactor, register, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
